import { Link } from "react-router-dom";
import ArrowRightIcon from "../../../assets/icons/arrow-right-desktop.svg";
import { useLoginForm } from "../hooks/useLoginForm";
import { OtpInput } from "../components/OtpInput";
import styles from "../Auth.module.css";

// Brand wordmark served from /public (PQ VPN Enterprise).
const LOGO_SRC = `${process.env.PUBLIC_URL}/Logo.svg`;

export default function LoginPage() {
  const {
    form, setField, showPassword, togglePassword,
    loading, error, info,
    mfaRequired, mfaCode, setMfaCode,
    secondsLeft, codeExpired, canResend, resendsLeft,
    submit, submitMfa, resendCode, cancelMfa,
  } = useLoginForm();

  const mm = Math.floor(secondsLeft / 60);
  const ss = String(secondsLeft % 60).padStart(2, "0");
  const countdownText = codeExpired
    ? "Code expired — request a new one."
    : `Code expires in ${mm}:${ss}`;
  const resendLabel = resendsLeft === 0
    ? "No resends left"
    : codeExpired
      ? `Resend code (${resendsLeft} left)`
      : "Resend code";

  return (
    <div className={styles.page}>
      <div className={styles.bg} />

      <div className={styles.loginCard}>

        {/* ── Logo ── */}
        <div className={styles.loginLogo}>
          <img src={LOGO_SRC} alt="PQ VPN Enterprise" />
        </div>

        {/* ── Dashed divider ── */}
        <div className={styles.loginDivider} />

        {/* ── Title ── */}
        <h2 className={styles.loginTitle}>
          {mfaRequired ? "Two-factor authentication" : "Sign in"}
        </h2>

        {/* ── Error banner ── */}
        {error && <div className={styles.errorBanner}>{error}</div>}

        {mfaRequired ? (
          /* ─── MFA form ─── */
          <form className={styles.loginForm} onSubmit={submitMfa}>
            {/* Helper / "code sent" message + countdown, spaced above the boxes */}
            <div className={styles.mfaIntro}>
              {info && <div className={styles.infoBanner}>{info}</div>}
              <p className={styles.mfaCountdown} data-expired={codeExpired}>{countdownText}</p>
            </div>

            <div className={styles.loginFormFields}>
              <OtpInput value={mfaCode} onChange={setMfaCode} length={6} disabled={loading} />
            </div>
            <button
              type="submit"
              className={styles.loginSubmitBtn}
              disabled={loading || codeExpired || mfaCode.length < 6}
            >
              {loading
                ? <span className={styles.spinner} />
                : <>
                  <span>Verify</span>
                  <img src={ArrowRightIcon} alt="" className={styles.loginBtnIcon} />
                </>}
            </button>

            {/* Resend / cancel */}
            <div className={styles.mfaActions}>
              <button
                type="button"
                className={styles.loginForgot}
                onClick={resendCode}
                disabled={!canResend}
              >
                {resendLabel}
              </button>
              <button
                type="button"
                className={styles.loginForgot}
                onClick={cancelMfa}
                disabled={loading}
              >
                Back to sign in
              </button>
            </div>
          </form>
        ) : (
          <>
            {/* ─── Login form ─── */}
            <form className={styles.loginForm} onSubmit={submit}>
              <div className={styles.loginFormFields}>
                {/* Email */}
                <div className={styles.floatingField}>
                  <input
                    id="email"
                    className={styles.loginInput}
                    type="email"
                    placeholder=" "
                    value={form.email}
                    onChange={setField("email")}
                    autoComplete="email"
                  />
                  <label htmlFor="email" className={styles.floatingLabel}>Email</label>
                </div>

                {/* Password */}
                <div className={styles.floatingField}>
                  <div className={styles.loginPassWrap}>
                    <input
                      id="password"
                      className={styles.loginInput}
                      type={showPassword ? "text" : "password"}
                      placeholder=" "
                      value={form.password}
                      onChange={setField("password")}
                      autoComplete="current-password"
                    />
                    <label htmlFor="password" className={styles.floatingLabel}>Password</label>
                    <button type="button" className={styles.loginEyeBtn} onClick={togglePassword}>
                      {showPassword
                        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>}
                    </button>
                  </div>
                </div>
              </div>

              {/* Forgot password */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 16 }}>
                <Link to="/forgot" className={styles.loginForgot}>Forgot password?</Link>
              </div>

              {/* Submit button */}
              <button type="submit" className={styles.loginSubmitBtn} disabled={loading}>
                {loading
                  ? <span className={styles.spinner} />
                  : <>
                    <span>Log in</span>
                    <img src={ArrowRightIcon} alt="" className={styles.loginBtnIcon} />
                  </>}
              </button>
            </form>
          </>
        )}
      </div>

      <footer className={styles.footer}>
        <span>© 2026 NPQ VPN. All rights reserved.</span>
        {/* Legal / Privacy / Compliance destinations are not provided by the VPN
            (Vaughn) side — there are no informational pages/URLs for these yet.
            Left as inert placeholders (href="#") until those pages exist. */}
        <div className={styles.footerLinks}>
          <a href="#">Legal</a><a href="#">Privacy</a><a href="#">Compliance</a>
        </div>
      </footer>
    </div>
  );
}
